import type { PropsWithChildren, ReactNode } from "react"
import { TooltipContent } from "./TooltipContent"
import { TooltipContext } from "./TooltipContext"
import { TooltipTrigger } from "./TooltipTrigger"
import { useTooltip } from "./useTooltip"
import type { UseTooltipOptions } from "./useTooltip"

interface TooltipProps {
	/**
	 * The content of the tooltip. When undefined, the tooltip will not show, but the children will still be rendered normally
	 */
	content: string | ReactNode | undefined

	/**
	 * The placement of the tooltip relative to its trigger.
	 * @default top
	 */
	placement?: "top" | "right" | "bottom" | "left"

	appendToBody?: boolean
}

/**
 * The `Tooltip` is a component for displaying more accurate information.
 * It can either be used by hovering over or clicking the custom __content__.
 *
 * Note: the wrapping structure (`TooltipContext.Provider` > `TooltipTrigger`) is always rendered,
 * even when `content` is falsy, only `TooltipContent` is conditionally rendered. This keeps the
 * position/type of `children` stable across renders so toggling `content` (e.g. a `disabledHint`
 * that appears/disappears at runtime) doesn't unmount/remount `children`, which would break stateful
 * children like web components. `useTooltip` is still told whether there's `content` via `enabled`
 * so it can skip its internal Effects/event listeners when there's nothing to show.
 *
 * Any extra rest props (e.g. `data-testid`) are only forwarded onto the trigger element while
 * `content` is present, matching the fact that they're meant to identify/augment the *active*
 * tooltip trigger rather than permanently overriding attributes on `children`.
 */
export const Tooltip = (props: TooltipProps & Required<PropsWithChildren> & Omit<UseTooltipOptions, "enabled">) => {
	const { appendToBody, children, content, placement, ...rest } = props
	const hasContent = !!content
	const tooltip = useTooltip({ enabled: hasContent, placement })

	return (
		<TooltipContext.Provider value={tooltip}>
			<TooltipTrigger {...(hasContent ? rest : {})}>{children}</TooltipTrigger>
			{hasContent && <TooltipContent appendToBody={appendToBody}>{content}</TooltipContent>}
		</TooltipContext.Provider>
	)
}
